import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Card,
  CardHeader,
  CardBody,
  Code,
  Alert,
  AlertIcon,
  List,
  ListItem,
  ListIcon,
  useColorModeValue,
  Divider,
  Badge,
  SimpleGrid,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Progress,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from '@chakra-ui/react';
import {
  CheckCircleIcon,
  WarningIcon,
  TimeIcon,
  InfoIcon,
  StarIcon,
  ViewIcon,
  ChevronRightIcon,
} from '@chakra-ui/icons';
import DocumentationLayout from '../../components/DocumentationLayout';

const UnderstandingResults: React.FC = () => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textSecondary = useColorModeValue('gray.600', 'gray.400');
  const codeBg = useColorModeValue('gray.100', 'gray.700');
  const accentBg = useColorModeValue('blue.50', 'blue.900');
  const successBg = useColorModeValue('green.50', 'green.900');
  const errorBg = useColorModeValue('red.50', 'red.900');

  const ResultCard: React.FC<{
    title: string;
    description: string;
    children: React.ReactNode;
    status?: 'success' | 'warning' | 'error' | 'info';
  }> = ({ title, description, children, status = 'info' }) => (
    <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
      <CardHeader>
        <VStack align="start" spacing={2}>
          <HStack>
            <Icon 
              as={status === 'success' ? CheckCircleIcon : status === 'warning' ? WarningIcon : InfoIcon} 
              color={`${status === 'success' ? 'green' : status === 'warning' ? 'orange' : status === 'error' ? 'red' : 'blue'}.500`}
            />
            <Heading size="md">{title}</Heading>
          </HStack>
          <Text fontSize="sm" color={textSecondary}>
            {description}
          </Text>
        </VStack>
      </CardHeader>
      <CardBody pt={0}>{children}</CardBody>
    </Card>
  );

  return (
    <DocumentationLayout
      title="Understanding Results"
      description="Learn how to interpret submission results, performance metrics, and visual feedback from the Speedcode platform."
    >
      <VStack spacing={8} align="stretch">
        {/* Introduction */}
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Box>
            <Text fontWeight="semibold">Reading Your Results</Text>
            <Text fontSize="sm">
              Speedcode provides detailed feedback on both correctness and performance. 
              Understanding these results is key to improving your solutions.
            </Text>
          </Box>
        </Alert>

        {/* Results Overview */}
        <Box>
          <Heading size="lg" mb={4}>Submission Results Overview</Heading>
          <Text color={textSecondary} mb={6}>
            When you submit a solution, you'll see several types of feedback. Here's what each section means:
          </Text>
          
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <Card bg={successBg} borderColor="green.200" borderWidth="1px">
              <CardBody>
                <VStack align="start" spacing={3}>
                  <HStack>
                    <Icon as={CheckCircleIcon} color="green.500" boxSize={6} />
                    <Heading size="md">Correctness Results</Heading>
                  </HStack>
                  <Text fontSize="sm">
                    Shows whether your solution produces the correct output for each test case.
                    This is binary: either pass ✓ or fail ✗.
                  </Text>
                </VStack>
              </CardBody>
            </Card>
            
            <Card bg={accentBg} borderColor="blue.200" borderWidth="1px">
              <CardBody>
                <VStack align="start" spacing={3}>
                  <HStack>
                    <Icon as={TimeIcon} color="blue.500" boxSize={6} />
                    <Heading size="md">Performance Metrics</Heading>
                  </HStack>
                  <Text fontSize="sm">
                    Detailed timing and efficiency measurements showing how fast your solution runs
                    compared to reference implementations.
                  </Text>
                </VStack>
              </CardBody>
            </Card>
          </SimpleGrid>
        </Box>

        {/* Correctness Results */}
        <ResultCard
          title="Correctness Results"
          description="Understanding test case outcomes and debugging failed cases"
          status="success"
        >
          <VStack spacing={4} align="stretch">
            <Box>
              <Text fontWeight="semibold" mb={3}>Test Case Status Indicators:</Text>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Symbol</Th>
                    <Th>Status</Th>
                    <Th>Meaning</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td>
                      <Icon as={CheckCircleIcon} color="green.500" />
                    </Td>
                    <Td>
                      <Badge colorScheme="green">PASS</Badge>
                    </Td>
                    <Td>Correct output produced</Td>
                  </Tr>
                  <Tr>
                    <Td>
                      <Icon as={WarningIcon} color="red.500" />
                    </Td>
                    <Td>
                      <Badge colorScheme="red">FAIL</Badge>
                    </Td>
                    <Td>Incorrect output or runtime error</Td>
                  </Tr>
                  <Tr>
                    <Td>
                      <Icon as={TimeIcon} color="orange.500" />
                    </Td>
                    <Td>
                      <Badge colorScheme="orange">TIMEOUT</Badge>
                    </Td>
                    <Td>Solution took too long to execute</Td>
                  </Tr>
                </Tbody>
              </Table>
            </Box>
            
            <Alert status="warning" variant="left-accent">
              <AlertIcon />
              <Box>
                <Text fontWeight="semibold">Common Failure Causes</Text>
                <List spacing={1} fontSize="sm" mt={2}>
                  <ListItem>• Wrong algorithm or logic errors</ListItem>
                  <ListItem>• Integer overflow on large inputs</ListItem>
                  <ListItem>• Array bounds violations</ListItem>
                  <ListItem>• Incorrect input/output formatting</ListItem>
                </List>
              </Box>
            </Alert>
          </VStack>
        </ResultCard>

        {/* Performance Metrics */}
        <ResultCard
          title="Performance Metrics"
          description="Detailed timing and efficiency measurements"
          status="info"
        >
          <VStack spacing={6} align="stretch">
            <Box>
              <Text fontWeight="semibold" mb={3}>Key Performance Indicators:</Text>
              
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Card bg={accentBg} p={4}>
                  <Stat>
                    <StatLabel>Runtime</StatLabel>
                    <StatNumber>1.234s</StatNumber>
                    <StatHelpText>Time to complete execution</StatHelpText>
                  </Stat>
                </Card>
                
                <Card bg={accentBg} p={4}>
                  <Stat>
                    <StatLabel>Speedup</StatLabel>
                    <StatNumber>3.2x</StatNumber>
                    <StatHelpText>vs. reference implementation</StatHelpText>
                  </Stat>
                </Card>
                
                <Card bg={accentBg} p={4}>
                  <Stat>
                    <StatLabel>Score</StatLabel>
                    <StatNumber>85/100</StatNumber>
                    <StatHelpText>Performance ranking</StatHelpText>
                  </Stat>
                </Card>
                
                <Card bg={accentBg} p={4}>
                  <Stat>
                    <StatLabel>Memory</StatLabel>
                    <StatNumber>256 MB</StatNumber>
                    <StatHelpText>Peak memory usage</StatHelpText>
                  </Stat>
                </Card>
              </SimpleGrid>
            </Box>
            
            <Divider />
            
            <Box>
              <Text fontWeight="semibold" mb={3}>Performance Score Calculation:</Text>
              <VStack spacing={3} align="start">
                <HStack width="100%">
                  <Text fontSize="sm" minW="120px">Correctness:</Text>
                  <Progress value={100} colorScheme="green" flex={1} />
                  <Text fontSize="sm" color="green.500">100%</Text>
                </HStack>
                
                <HStack width="100%">
                  <Text fontSize="sm" minW="120px">Speed:</Text>
                  <Progress value={85} colorScheme="blue" flex={1} />
                  <Text fontSize="sm" color="blue.500">85%</Text>
                </HStack>
                
                <HStack width="100%">
                  <Text fontSize="sm" minW="120px">Efficiency:</Text>
                  <Progress value={70} colorScheme="purple" flex={1} />
                  <Text fontSize="sm" color="purple.500">70%</Text>
                </HStack>
                
                <HStack width="100%">
                  <Text fontSize="sm" minW="120px" fontWeight="bold">Overall:</Text>
                  <Progress value={85} colorScheme="orange" flex={1} />
                  <Text fontSize="sm" color="orange.500" fontWeight="bold">85/100</Text>
                </HStack>
              </VStack>
            </Box>
            
            <Alert status="info" variant="left-accent">
              <AlertIcon />
              <Box>
                <Text fontWeight="semibold">Score Components</Text>
                <List spacing={1} fontSize="sm" mt={2}>
                  <ListItem>• <strong>Correctness (40%)</strong>: Must pass all test cases</ListItem>
                  <ListItem>• <strong>Speed (40%)</strong>: Runtime vs. reference solution</ListItem>
                  <ListItem>• <strong>Efficiency (20%)</strong>: Memory usage and parallel scaling</ListItem>
                </List>
              </Box>
            </Alert>
          </VStack>
        </ResultCard>

        {/* Benchmark Tables */}
        <ResultCard
          title="Benchmark Results Table"
          description="Detailed per-dataset performance breakdown"
        >
          <VStack spacing={4} align="stretch">
            <Text color={textSecondary}>
              For problems with multiple test datasets, you'll see a table showing performance on each:
            </Text>
            
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Dataset</Th>
                    <Th>Status</Th>
                    <Th>Your Time</Th>
                    <Th>Reference</Th>
                    <Th>Speedup</Th>
                    <Th>Score</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td><Code fontSize="xs">small_01</Code></Td>
                    <Td><Badge colorScheme="green">PASS</Badge></Td>
                    <Td>0.025s</Td>
                    <Td>0.030s</Td>
                    <Td>1.2x</Td>
                    <Td>95</Td>
                  </Tr>
                  <Tr>
                    <Td><Code fontSize="xs">medium_03</Code></Td>
                    <Td><Badge colorScheme="green">PASS</Badge></Td>
                    <Td>1.234s</Td>
                    <Td>3.456s</Td>
                    <Td>2.8x</Td>
                    <Td>88</Td>
                  </Tr>
                  <Tr>
                    <Td><Code fontSize="xs">large_07</Code></Td>
                    <Td><Badge colorScheme="green">PASS</Badge></Td>
                    <Td>12.45s</Td>
                    <Td>45.67s</Td>
                    <Td>3.7x</Td>
                    <Td>92</Td>
                  </Tr>
                  <Tr bg={errorBg}>
                    <Td><Code fontSize="xs">xlarge_10</Code></Td>
                    <Td><Badge colorScheme="orange">TIMEOUT</Badge></Td>
                    <Td>{'>'} 30.0s</Td>
                    <Td>28.9s</Td>
                    <Td>0.96x</Td>
                    <Td>0</Td>
                  </Tr>
                </Tbody>
              </Table>
            </Box>
            
            <Alert status="warning" variant="left-accent">
              <AlertIcon />
              <Box>
                <Text fontWeight="semibold">Understanding Timeouts</Text>
                <Text fontSize="sm">
                  A timeout means your solution didn't complete within the time limit. 
                  This often indicates the need for algorithmic improvements rather than just code optimization.
                </Text>
              </Box>
            </Alert>
          </VStack>
        </ResultCard>

        {/* Compiler Output */}
        <ResultCard
          title="Compiler Output"
          description="Build logs and compilation messages"
        >
          <VStack spacing={4} align="stretch">
            <Text color={textSecondary}>
              The platform shows you the compiler output, including any warnings or errors:
            </Text>
            
            <Box bg={codeBg} p={4} borderRadius="md" fontSize="sm" fontFamily="monospace">
              <Text color="green.500">✓ Compilation successful</Text>
              <Text color={textSecondary}>g++ -O3 -fopencilk -march=native solution.cpp -o solution</Text>
              <Text color="orange.500">warning: unused variable 'temp' [-Wunused-variable]</Text>
              <Text color={textSecondary}>Optimization flags: -O3 -ffast-math -funroll-loops</Text>
            </Box>
            
            <Alert status="info" variant="left-accent">
              <AlertIcon />
              <Box>
                <Text fontWeight="semibold">Compilation Notes</Text>
                <List spacing={1} fontSize="sm" mt={2}>
                  <ListItem>• Solutions are compiled with aggressive optimizations (-O3)</ListItem>
                  <ListItem>• OpenCilk parallel runtime is automatically linked</ListItem>
                  <ListItem>• Warnings don't prevent submission but may indicate issues</ListItem>
                </List>
              </Box>
            </Alert>
          </VStack>
        </ResultCard>

        {/* Console Output */}
        <ResultCard
          title="Console Output & Debugging"
          description="stdout, stderr, and debugging information"
        >
          <VStack spacing={4} align="stretch">
            <Text color={textSecondary}>
              You can see any output your program produces during execution:
            </Text>
            
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box>
                <Text fontWeight="semibold" mb={2}>Standard Output:</Text>
                <Box bg={codeBg} p={3} borderRadius="md" fontSize="sm" fontFamily="monospace">
                  <Text>Debug: Processing 1000000 elements</Text>
                  <Text>Debug: Using 8 threads</Text>
                  <Text>Result: 500000500000</Text>
                </Box>
              </Box>
              
              <Box>
                <Text fontWeight="semibold" mb={2}>Error Output:</Text>
                <Box bg={errorBg} p={3} borderRadius="md" fontSize="sm" fontFamily="monospace">
                  <Text color="red.600">Warning: Stack size may be insufficient</Text>
                  <Text color="red.600">Cilksan: No races detected</Text>
                </Box>
              </Box>
            </SimpleGrid>
            
            <Alert status="warning" variant="left-accent">
              <AlertIcon />
              <Box>
                <Text fontWeight="semibold">Debug Output Tips</Text>
                <List spacing={1} fontSize="sm" mt={2}>
                  <ListItem>• Use <Code>std::cerr</Code> for debug messages (won't affect output checking)</ListItem>
                  <ListItem>• Remove debug output before final submission for better performance</ListItem>
                  <ListItem>• Large amounts of output can slow down your solution</ListItem>
                </List>
              </Box>
            </Alert>
          </VStack>
        </ResultCard>

        {/* Improvement Suggestions */}
        <Box>
          <Divider mb={6} />
          <Heading size="lg" mb={4}>Improving Your Solutions</Heading>
          
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
              <CardBody>
                <VStack align="start" spacing={3}>
                  <Icon as={StarIcon} color="yellow.500" boxSize={6} />
                  <Heading size="md">Failed Test Cases</Heading>
                  <Text fontSize="sm" color={textSecondary}>
                    Review the specific inputs that failed. Often the pattern reveals 
                    edge cases or algorithmic issues.
                  </Text>
                </VStack>
              </CardBody>
            </Card>
            
            <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
              <CardBody>
                <VStack align="start" spacing={3}>
                  <Icon as={TimeIcon} color="blue.500" boxSize={6} />
                  <Heading size="md">Performance Issues</Heading>
                  <Text fontSize="sm" color={textSecondary}>
                    If you're timing out on large inputs, consider algorithmic complexity 
                    improvements before micro-optimizations.
                  </Text>
                </VStack>
              </CardBody>
            </Card>
            
            <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
              <CardBody>
                <VStack align="start" spacing={3}>
                  <Icon as={ViewIcon} color="green.500" boxSize={6} />
                  <Heading size="md">Parallel Efficiency</Heading>
                  <Text fontSize="sm" color={textSecondary}>
                    Low speedup often indicates insufficient parallelization or 
                    too much parallel overhead for the problem size.
                  </Text>
                </VStack>
              </CardBody>
            </Card>
          </SimpleGrid>
        </Box>
      </VStack>
    </DocumentationLayout>
  );
};

export default UnderstandingResults; 